import * as React from 'react';

import BooksSelect from '../../../../components/books/common/inputs/BooksSelect';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksCheckInput, {
  InputType,
} from '../../../../components/books/model1/forms/BooksCheckInput';
import BooksTextInput, {
  TextInputType,
} from '../../../../components/books/model1/forms/BooksTextInput';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import { InputValueType } from './InputUserInfo';
import ErrorMessage from './InputUserInfoErrorMessage';

type ResponsibleElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;

interface Props {
  questionnaires: IBooks.Questionnaire[];
  onChange: (name: string, value: InputValueType) => void;
  namePrefix: 'questionnaires';
  values: { [name: string]: InputValueType };
}

interface ChildProps {
  onChange: (e: React.ChangeEvent<ResponsibleElement>) => void;
  values: { [name: string]: InputValueType };
}

interface TextProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireText;
}

interface TextAreaProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireTextArea;
}

interface SelectProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireSelect;
}

interface RadioProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireRadio;
}

interface CheckboxsProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireCheckbox;
}

interface CheckboxProps extends ChildProps {
  option: { label: string; value: string };
  customFieldId: number;
}

export const submittable = (
  questionnaires?: IBooks.Questionnaire[],
  values?: { [name: string]: InputValueType },
): boolean => {
  if (!questionnaires) {
    return true;
  }
  const requiredQuestionnaires = questionnaires.filter(q => q.required);
  if (requiredQuestionnaires.length === 0) {
    return true;
  }
  if (!values) {
    return false;
  }
  return requiredQuestionnaires.every(q => {
    const v = values[q.customFieldId];
    switch (typeof v) {
      case 'string':
        return !!v;
      case 'undefined':
        return false;
      case 'object': // questionnaire checkboxの場合
        return Object.values(v as { [v: string]: boolean }).some(b => b);
      default:
        throw new Error(`something wrong with ${v}`);
    }
  });
};

const Text: React.SFC<TextProps> = ({ questionnaire, onChange, values }) => (
  <BooksTextInput
    type={TextInputType.Text}
    name={`${questionnaire.customFieldId}`}
    value={(values[questionnaire.customFieldId] as string) || ''}
    onChange={onChange}
  />
);

const TextArea: React.SFC<TextAreaProps> = ({
  questionnaire,
  onChange,
  values,
}) => (
  <BooksTextInput
    type={TextInputType.TextArea}
    rows={6}
    name={`${questionnaire.customFieldId}`}
    value={(values[questionnaire.customFieldId] as string) || ''}
    onChange={onChange}
  />
);

const Select: React.SFC<SelectProps> = ({
  questionnaire,
  onChange,
  values,
}) => {
  return (
    <BooksSelect
      name={`${questionnaire.customFieldId}`}
      value={(values[questionnaire.customFieldId] as string) || ''}
      onChange={onChange}
      selected={(values[questionnaire.customFieldId] as string) !== undefined}
    >
      {questionnaire.options.map((o, i) => (
        <option key={i} value={o.value}>
          {o.label}
        </option>
      ))}
    </BooksSelect>
  );
};

const Radio: React.SFC<RadioProps> = ({ questionnaire, onChange, values }) => {
  return (
    <div>
      {questionnaire.options.map((o, i) => (
        <BooksCheckInput
          key={i}
          type={InputType.Radio}
          name={`${questionnaire.customFieldId}`}
          value={o.value}
          onChange={onChange}
          checked={
            ((values[questionnaire.customFieldId] as string) || '') === o.value
          }
        >
          {o.label}
        </BooksCheckInput>
      ))}
    </div>
  );
};

const Checkbox: React.SFC<CheckboxProps> = ({
  option,
  customFieldId,
  onChange,
  values,
}) => {
  const currentValues = values[customFieldId] || {};
  const checked = !!currentValues[option.value];
  return (
    <BooksCheckInput
      type={InputType.Checkbox}
      name={
        // 最終的に送るのはvalueのみでいいので
        // { value: boolean } の構造体でcheck状態を管理する
        //
        // 実際は少し複雑でこんな感じ
        // { 123: { 'a': true, 'b': false } }
        //
        // この構造体のうち{'b': false}を置き換えるため
        // 以下のような名前にする
        // 123.b
        `${customFieldId}.${option.value}`
      }
      value={option.value}
      onChange={onChange}
      checked={checked}
    >
      {option.label}
    </BooksCheckInput>
  );
};

const Checkboxs: React.SFC<CheckboxsProps> = ({
  questionnaire,
  onChange,
  values,
}) => {
  return (
    <>
      {questionnaire.options.map((o, i) => (
        <Checkbox
          key={i}
          customFieldId={questionnaire.customFieldId}
          option={o}
          onChange={onChange}
          values={values}
        />
      ))}
    </>
  );
};

const isText = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireText =>
  q.type === 'text';

const isTextArea = (
  q: IBooks.Questionnaire,
): q is IBooks.QuestionnaireTextArea => q.type === 'text_area';

const isSelect = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireSelect =>
  q.type === 'select';

const isRadio = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireRadio =>
  q.type === 'radio';

const isCheckbox = (
  q: IBooks.Questionnaire,
): q is IBooks.QuestionnaireCheckbox => q.type === 'checkbox';

class InputUserInfoQuestionnaires extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.renderQuestionnaire = this.renderQuestionnaire.bind(this);
  }

  private handleChange(e: React.ChangeEvent<ResponsibleElement>) {
    const {
      currentTarget: { name, value, type },
    } = e;
    const { namePrefix } = this.props;

    if (type === 'checkbox') {
      const {
        currentTarget: { checked },
      } = e as React.ChangeEvent<HTMLInputElement>;
      this.props.onChange(`${namePrefix}.${name}`, checked);
    } else {
      this.props.onChange(`${namePrefix}.${name}`, value);
    }
  }

  private renderQuestionnaire(questionnaire: IBooks.Questionnaire) {
    if (isText(questionnaire)) {
      return (
        <Text
          {...this.props}
          key={questionnaire.customFieldId}
          questionnaire={questionnaire}
          onChange={this.handleChange}
        />
      );
    }
    if (isTextArea(questionnaire)) {
      return (
        <TextArea
          {...this.props}
          key={questionnaire.customFieldId}
          questionnaire={questionnaire}
          onChange={this.handleChange}
        />
      );
    }
    if (isSelect(questionnaire)) {
      return (
        <Select
          {...this.props}
          key={questionnaire.customFieldId}
          questionnaire={questionnaire}
          onChange={this.handleChange}
        />
      );
    }
    if (isRadio(questionnaire)) {
      return (
        <Radio
          {...this.props}
          key={questionnaire.customFieldId}
          questionnaire={questionnaire}
          onChange={this.handleChange}
        />
      );
    }
    if (isCheckbox(questionnaire)) {
      return (
        <Checkboxs
          {...this.props}
          key={questionnaire.customFieldId}
          questionnaire={questionnaire}
          onChange={this.handleChange}
        />
      );
    }
    throw new Error();
  }

  public render() {
    const { questionnaires } = this.props;
    return questionnaires.map((q, i) => (
      <div key={i}>
        <BooksLabeledContainer
          label={q.label}
          requiredLabel={q.required}
          paddingTop={true}
          errorStyle={q.errors && q.errors.length !== 0}
        >
          {this.renderQuestionnaire(q)}
        </BooksLabeledContainer>
        <ErrorMessage fieldSource={q} />
      </div>
    ));
  }
}

export default InputUserInfoQuestionnaires;
